const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
const scenes = [...document.querySelectorAll("[data-sequence]")];
const loader = document.querySelector("[data-loader]");
const loaderPercent = document.querySelector("[data-loader-percent]");
const loaderLine = document.querySelector("[data-loader-line]");
const cinematicRoot = document.querySelector("[data-hvv-root]");
const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

function setLoadProgress(loaded, total) {
  const progress = total ? loaded / total : 1;
  const value = Math.round(progress * 100);
  loaderPercent.textContent = `${value}%`;
  loaderLine.style.transform = `scaleX(${progress})`;
}

function loadImage(src) {
  return new Promise((resolve) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

async function preloadCompactAssets() {
  const videos = [...document.querySelectorAll("[data-video] source")];
  const sources = [cinematicRoot?.dataset.master, ...videos.map((source) => source.src)].filter(Boolean);
  let loaded = 0;
  setLoadProgress(0, sources.length);

  await Promise.all(sources.map((src) => new Promise((resolve) => {
    if (/\.mp4(?:$|\?)/.test(src)) {
      const video = document.createElement("video");
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        loaded += 1;
        setLoadProgress(loaded, sources.length);
        resolve();
      };
      video.addEventListener("loadedmetadata", finish, { once: true });
      video.addEventListener("error", finish, { once: true });
      video.preload = "metadata";
      video.src = src;
      video.load();
      setTimeout(finish, 7000);
    } else {
      loadImage(src).then(() => { loaded += 1; setLoadProgress(loaded, sources.length); resolve(); });
    }
  })));
}

function sceneProgress(scene) {
  const rect = scene.getBoundingClientRect();
  return clamp(-rect.top / Math.max(1, scene.offsetHeight - window.innerHeight));
}

function updateTimedElements(scene, progress) {
  const timed = [...scene.querySelectorAll("[data-at]")];
  timed.forEach((element, index) => {
    const at = Number(element.dataset.at || 0);
    const next = timed[index + 1] ? Number(timed[index + 1].dataset.at) : 1.08;
    const visible = progress >= at - 0.035 && progress < next + 0.08;
    element.classList.toggle("is-active", visible);
  });
}

function setupReducedSequences() {
  scenes.forEach((scene) => {
    const video = scene.querySelector("[data-video]");
    if (video) {
      video.removeAttribute("autoplay");
      video.loop = false;
      video.pause();
    }
    scene.querySelectorAll("[data-at]").forEach((element) => {
      element.classList.add("is-active");
    });
  });
  return () => {};
}

function setupCompactSequences() {
  let lastScrollY = -1;
  let lastViewportHeight = -1;

  const states = scenes.map((scene) => {
    const video = scene.querySelector("[data-video]");
    const hasSource = Boolean(video?.querySelector("source"));
    const state = {
      scene,
      video,
      hasSource,
      ready: false,
      duration: 0,
      current: 0,
      target: 0,
      lastSeek: -1,
      scrubStart: clamp(Number(scene.dataset.scrubStart || 0)),
      scrubEnd: clamp(Number(scene.dataset.scrubEnd || 1)),
    };

    if (!hasSource) return state;

    video.removeAttribute("autoplay");
    video.loop = false;
    video.controls = false;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.preload = "auto";

    const stopPlayback = () => {
      if (!video.paused) video.pause();
    };
    video.addEventListener("play", stopPlayback);

    const markReady = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      state.duration = video.duration;
      state.ready = true;
      const progress = sceneProgress(scene);
      const directed = scene.dataset.direction === "reverse" ? 1 - progress : progress;
      const scrubProgress = state.scrubStart + directed * (state.scrubEnd - state.scrubStart);
      state.current = scrubProgress * state.duration;
      state.target = state.current;
      const initialTime = clamp(state.current, 0.01, Math.max(0.01, state.duration - 0.04));
      try {
        video.currentTime = initialTime;
        state.lastSeek = initialTime;
      } catch (_) {
        // Safari may defer the first seek until its media range is available.
      }
      stopPlayback();
    };

    if (video.readyState >= 1) markReady();
    else video.addEventListener("loadedmetadata", markReady, { once: true });
    video.load();
    return state;
  });

  const updateTargets = () => {
    states.forEach((state) => {
      const progress = sceneProgress(state.scene);
      const directed = state.scene.dataset.direction === "reverse" ? 1 - progress : progress;
      updateTimedElements(state.scene, progress);
      if (state.ready) {
        const scrubProgress = state.scrubStart + directed * (state.scrubEnd - state.scrubStart);
        state.target = scrubProgress * state.duration;
      }
    });
  };

  const render = () => {
    if (window.scrollY !== lastScrollY || window.innerHeight !== lastViewportHeight) {
      lastScrollY = window.scrollY;
      lastViewportHeight = window.innerHeight;
      updateTargets();
    }

    states.forEach((state) => {
      if (!state.ready || !state.hasSource) return;
      if (state.video.seeking) return;
      state.current += (state.target - state.current) * 0.3;
      const seekTime = clamp(state.current, 0.01, Math.max(0.01, state.duration - 0.04));
      if (Math.abs(seekTime - state.lastSeek) < 0.018) return;
      try {
        state.video.currentTime = seekTime;
        state.lastSeek = seekTime;
      } catch (_) {
        // Keep the previous decoded frame until Safari accepts another seek.
      }
    });
    requestAnimationFrame(render);
  };

  window.addEventListener("resize", updateTargets, { passive: true });
  document.addEventListener("visibilitychange", () => {
    states.forEach((state) => state.video?.pause());
  });
  updateTargets();
  render();
  return updateTargets;
}

function setupSequences() {
  if (reducedQuery.matches) return setupReducedSequences();
  return setupCompactSequences();
}

function setupRevealObserver() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: "0px 0px -7%" });
  document.querySelectorAll(".reveal, .line-reveal").forEach((element) => observer.observe(element));
}

function setupPageProgress() {
  const progressBar = document.querySelector("[data-page-progress]");
  const update = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    progressBar.style.transform = `scaleX(${scrollable > 0 ? window.scrollY / scrollable : 0})`;
  };
  window.addEventListener("scroll", update, { passive: true });
  update();
}

function setupCursor() {
  if (!window.matchMedia("(pointer: fine)").matches || reducedQuery.matches) return;
  const cursor = document.querySelector("[data-cursor]");
  let x = -100;
  let y = -100;
  let renderedX = x;
  let renderedY = y;
  window.addEventListener("pointermove", (event) => { x = event.clientX; y = event.clientY; }, { passive: true });
  document.querySelectorAll("a, button, summary").forEach((element) => {
    element.addEventListener("pointerenter", () => cursor.classList.add("is-active"));
    element.addEventListener("pointerleave", () => cursor.classList.remove("is-active"));
  });
  const animate = () => {
    renderedX += (x - renderedX) * .2;
    renderedY += (y - renderedY) * .2;
    cursor.style.transform = `translate3d(${renderedX}px, ${renderedY}px, 0) translate(-50%, -50%)`;
    requestAnimationFrame(animate);
  };
  animate();
}

function setupMagneticElements() {
  if (!window.matchMedia("(pointer: fine)").matches || reducedQuery.matches) return;
  document.querySelectorAll(".magnetic").forEach((element) => {
    element.addEventListener("pointermove", (event) => {
      const rect = element.getBoundingClientRect();
      const x = (event.clientX - rect.left - rect.width / 2) * .12;
      const y = (event.clientY - rect.top - rect.height / 2) * .18;
      element.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });
    element.addEventListener("pointerleave", () => { element.style.transform = ""; });
  });
}

function setupOffers() {
  const offers = [...document.querySelectorAll("[data-offer]")];
  const summary = document.querySelector("[data-offer-summary]");
  const shopAction = document.querySelector("[data-shop-action]");
  const select = (selected) => {
    offers.forEach((offer) => {
      const active = offer === selected;
      offer.classList.toggle("is-selected", active);
      offer.setAttribute("aria-checked", String(active));
      offer.querySelector("i").textContent = active ? "Selected" : "Select";
      offer.tabIndex = active ? 0 : -1;
    });
    summary.textContent = selected.dataset.offer;
    shopAction.href = selected.dataset.checkoutUrl;
    shopAction.setAttribute("aria-label", `Shop ${selected.dataset.offer} on HERBIVIVE`);
  };
  offers.forEach((offer, index) => {
    offer.addEventListener("click", () => select(offer));
    offer.addEventListener("keydown", (event) => {
      if (!["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"].includes(event.key)) return;
      event.preventDefault();
      const delta = event.key === "ArrowDown" || event.key === "ArrowRight" ? 1 : -1;
      const next = offers[(index + delta + offers.length) % offers.length];
      select(next);
      next.focus();
    });
  });
  const selectedOffer = offers.find((offer) => offer.getAttribute("aria-checked") === "true") || offers[0];
  if (selectedOffer) select(selectedOffer);
}

function setupLenis() {
  if (reducedQuery.matches || !window.Lenis) return;
  const lenis = new window.Lenis({ duration: 1.05, smoothWheel: true, wheelMultiplier: .9 });
  const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
  requestAnimationFrame(raf);
  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener("click", (event) => {
      const target = document.querySelector(link.getAttribute("href"));
      if (!target) return;
      event.preventDefault();
      lenis.scrollTo(target, { offset: 0 });
    });
  });
}

async function boot() {
  const minimumDisplay = delay(900);
  const assets = preloadCompactAssets();
  const assetGate = Promise.race([assets, delay(8000)]);
  const fontGate = Promise.race([document.fonts?.ready || Promise.resolve(), delay(3000)]);
  await Promise.all([assetGate, minimumDisplay, fontGate]);

  setupSequences();
  setupRevealObserver();
  setupPageProgress();
  setupCursor();
  setupMagneticElements();
  setupOffers();
  setupLenis();

  document.body.classList.remove("is-loading");
  loader.classList.add("is-complete");
  setTimeout(() => loader.remove(), 850);
}

boot().catch((error) => {
  console.error("Cinematic boot fallback", error);
  document.body.classList.remove("is-loading");
  loader.classList.add("is-complete");
  setupRevealObserver();
  setupPageProgress();
  setupOffers();
});
